import { User, Session } from '@supabase/supabase-js';
import { createBrowserClient } from '@supabase/ssr';
// Define a UserProfile interface for this service
interface UserProfile {
  id: string;
  auth_id: string;
  name: string;
  handle: string;
  avatar_url?: string;
  email?: string;
  created_at: string;
  updated_at: string;
}
import { createLogger } from '@/lib/utils/logger';

const logger = createLogger('auth:service');

/**
 * Response type for profile API
 */
export interface ProfileResponse {
  authUser: User;
  profile: UserProfile | null;
  profileStatus: 'complete' | 'missing';
  error?: string;
}

/**
 * Error types for authentication
 */
export enum AuthErrorType {
  NETWORK_ERROR = 'network_error',
  AUTHENTICATION_ERROR = 'authentication_error',
  PROFILE_ERROR = 'profile_error',
  SERVER_ERROR = 'server_error',
  UNKNOWN_ERROR = 'unknown_error'
}

/**
 * Standardized error format for auth operations
 */
export interface AuthError {
  type: AuthErrorType;
  message: string;
  status?: number;
  originalError?: any;
}

/**
 * Cache configuration
 */
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const PROFILE_CACHE_KEY = 'auth_profile_cache';
const SESSION_CACHE_KEY = 'auth_session_cache';

/**
 * Get Supabase client
 */
function getSupabaseClient() {
  return createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!
  );
}

/**
 * Centralized Authentication Service
 * 
 * This service handles all authentication-related operations and provides
 * a consistent interface for the rest of the application.
 */
export const AuthService = {
  /**
   * Get the current user's profile from the API
   * Uses caching to minimize API calls
   */
  getProfile: async (bypassCache = false): Promise<ProfileResponse | null> => {
    try {
      // Check cache first (unless bypassing)
      if (!bypassCache && typeof localStorage !== 'undefined') {
        const cachedProfile = localStorage.getItem(PROFILE_CACHE_KEY);
        if (cachedProfile) {
          try {
            const { data, timestamp } = JSON.parse(cachedProfile);
            // Only use cache if it's not expired
            if (Date.now() - timestamp < CACHE_DURATION) {
              logger.debug('Using cached profile data');
              return data;
            } else {
              logger.debug('Cache expired, fetching fresh profile');
              localStorage.removeItem(PROFILE_CACHE_KEY);
            }
          } catch (e) {
            logger.warn('Error parsing cached profile:', { error: e });
            localStorage.removeItem(PROFILE_CACHE_KEY);
          }
        }
      }
      
      // Fetch fresh profile from API with credentials included to ensure cookies are sent
      const response = await fetch('/api/auth/profile', {
        credentials: 'include' // Critical fix: Ensure cookies are sent with the request
      });
      
      // Handle HTTP errors
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        
        // Special handling for 401 Unauthorized
        if (response.status === 401) {
          logger.warn('Not authenticated');
          return null;
        }
        
        // Handle 404 Not Found (profile missing)
        if (response.status === 404) {
          return errorData as ProfileResponse; // Return the partial response
        }
        
        const authError = {
          type: response.status === 500 ? AuthErrorType.SERVER_ERROR : AuthErrorType.PROFILE_ERROR,
          message: errorData.error || 'Error fetching profile',
          status: response.status,
          originalError: errorData
        };
        
        throw authError;
      }
      
      // Parse and cache the profile data
      const profileData = await response.json();
      
      // Cache the result
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({
          data: profileData,
          timestamp: Date.now()
        }));
      }
      
      return profileData;
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        logger.error('Network error fetching profile:', { error });
        throw {
          type: AuthErrorType.NETWORK_ERROR,
          message: 'Network error, please check your connection',
          originalError: error
        };
      }
      
      // Handle already formatted errors
      if (error && typeof error === 'object' && 'type' in error) {
        throw error;
      }
      
      // Handle unknown errors
      logger.error('Unknown error fetching profile:', { error });
      throw {
        type: AuthErrorType.UNKNOWN_ERROR,
        message: 'An unexpected error occurred',
        originalError: error
      };
    }
  },
  
  /**
   * Get the current session
   * Returns the session object if authenticated, null otherwise
   */
  getSession: async (bypassCache = false): Promise<Session | null> => {
    try {
      // Check cache first (unless bypassing)
      if (!bypassCache && typeof localStorage !== 'undefined') {
        const cachedSession = localStorage.getItem(SESSION_CACHE_KEY);
        if (cachedSession) {
          try {
            const { data, timestamp } = JSON.parse(cachedSession);
            // Only use cache if it's not expired
            if (Date.now() - timestamp < CACHE_DURATION) {
              logger.debug('Using cached session data');
              return data;
            } else {
              logger.debug('Session cache expired, fetching fresh session');
              localStorage.removeItem(SESSION_CACHE_KEY);
            }
          } catch (e) {
            logger.warn('Error parsing cached session:', { error: e });
            localStorage.removeItem(SESSION_CACHE_KEY);
          }
        }
      }
      
      // Get fresh session from Supabase
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        logger.error('Error getting session:', { error: error.message });
        throw {
          type: AuthErrorType.AUTHENTICATION_ERROR,
          message: error.message,
          originalError: error
        };
      }
      
      // Cache the result
      if (data.session && typeof localStorage !== 'undefined') {
        localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({
          data: data.session,
          timestamp: Date.now()
        }));
      }
      
      return data.session;
    } catch (error) {
      // Handle already formatted errors
      if (error && typeof error === 'object' && 'type' in error) {
        throw error;
      }
      
      logger.error('Error in getSession:', { error });
      throw {
        type: AuthErrorType.UNKNOWN_ERROR,
        message: 'An unexpected error occurred',
        originalError: error
      };
    }
  },
  
  /**
   * Refresh the current session
   */
  refreshSession: async (): Promise<Session | null> => {
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        logger.error('Error refreshing session:', { error: error.message });
        throw {
          type: AuthErrorType.AUTHENTICATION_ERROR,
          message: error.message,
          originalError: error
        };
      }
      
      // Update cached session
      if (data.session && typeof localStorage !== 'undefined') {
        localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({
          data: data.session,
          timestamp: Date.now()
        }));
        
        // Clear profile cache to force reload with new session
        localStorage.removeItem(PROFILE_CACHE_KEY);
      }
      
      return data.session;
    } catch (error) {
      // Handle already formatted errors
      if (error && typeof error === 'object' && 'type' in error) {
        throw error;
      }
      
      logger.error('Error in refreshSession:', { error });
      throw {
        type: AuthErrorType.UNKNOWN_ERROR,
        message: 'An unexpected error occurred',
        originalError: error
      };
    }
  },
  
  /**
   * Sign in with one-time password (email magic link)
   */
  signInWithOtp: async (email: string): Promise<boolean> => {
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/api/auth/callback`
        }
      });
      
      if (error) {
        logger.error('Error signing in with OTP:', { error: error.message });
        throw {
          type: AuthErrorType.AUTHENTICATION_ERROR,
          message: error.message,
          originalError: error
        };
      }
      
      return true;
    } catch (error) {
      // Handle already formatted errors
      if (error && typeof error === 'object' && 'type' in error) {
        throw error;
      }
      
      logger.error('Error in signInWithOtp:', { error });
      throw {
        type: AuthErrorType.UNKNOWN_ERROR,
        message: 'An unexpected error occurred',
        originalError: error
      };
    }
  },
  
  /**
   * Verify one-time password (email magic link)
   */
  verifyOtp: async (email: string, token: string): Promise<{ user: User | null; session: Session | null; isNewUser?: boolean }> => {
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email'
      });
      
      if (error) {
        logger.error('Error verifying OTP:', { error: error.message });
        throw {
          type: AuthErrorType.AUTHENTICATION_ERROR,
          message: error.message,
          originalError: error
        };
      }
      
      // Cache the session
      if (data.session && typeof localStorage !== 'undefined') {
        localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify({
          data: data.session,
          timestamp: Date.now()
        }));
      }
      
      // Check if this is a new user (no profile)
      let isNewUser = false;
      if (data.user) {
        try {
          const profileResponse = await AuthService.getProfile(true); // Bypass cache to get fresh profile
          isNewUser = !profileResponse || (profileResponse.profileStatus === 'missing');
        } catch (e) {
          logger.warn('Error checking if user is new:', { error: e });
          // Don't throw here, we'll assume not a new user
        }
      }
      
      return {
        user: data.user,
        session: data.session,
        isNewUser
      };
    } catch (error) {
      // Handle already formatted errors
      if (error && typeof error === 'object' && 'type' in error) {
        throw error;
      }
      
      logger.error('Error in verifyOtp:', { error });
      throw {
        type: AuthErrorType.UNKNOWN_ERROR,
        message: 'An unexpected error occurred',
        originalError: error
      };
    }
  },
  
  /**
   * Sign out the current user
   */
  signOut: async (): Promise<void> => {
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        logger.error('Error signing out:', { error: error.message });
        throw {
          type: AuthErrorType.AUTHENTICATION_ERROR,
          message: error.message,
          originalError: error
        };
      }
      
      // Clear cached data
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(PROFILE_CACHE_KEY);
        localStorage.removeItem(SESSION_CACHE_KEY);
      }
    } catch (error) {
      // Handle already formatted errors
      if (error && typeof error === 'object' && 'type' in error) {
        throw error;
      }
      
      logger.error('Error in signOut:', { error });
      throw {
        type: AuthErrorType.UNKNOWN_ERROR,
        message: 'An unexpected error occurred',
        originalError: error
      };
    }
  },
  
  /**
   * Create a new user profile
   * Used when a user signs in for the first time
   */
  createUserProfile: async (userData: { name: string; handle: string }): Promise<UserProfile> => {
    try {
      const response = await fetch('/api/auth/create-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData),
        credentials: 'include' // Critical fix: Include credentials to send auth cookies
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw {
          type: AuthErrorType.PROFILE_ERROR,
          message: errorData.error || 'Error creating profile',
          status: response.status,
          originalError: errorData
        };
      }
      
      // Clear profile cache to force reload with new profile
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(PROFILE_CACHE_KEY);
      }
      
      // Return the new profile
      const profileData = await response.json();
      return profileData.profile;
    } catch (error) {
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        logger.error('Network error creating profile:', { error });
        throw {
          type: AuthErrorType.NETWORK_ERROR,
          message: 'Network error, please check your connection',
          originalError: error
        };
      }
      
      // Handle already formatted errors
      if (error && typeof error === 'object' && 'type' in error) {
        throw error;
      }
      
      logger.error('Error in createUserProfile:', { error });
      throw {
        type: AuthErrorType.UNKNOWN_ERROR,
        message: 'An unexpected error occurred',
        originalError: error
      };
    }
  }
};